// Comprehensive Iowa family law data based on FindLaw and official Iowa sources
const iowaData = {
  'iowa': {
    name: 'Iowa',
    overview: {
      keyPoints: [
        'Iowa follows the "best interests of the child" standard with specific factors outlined in Iowa Code Chapter 598',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'Iowa uses traditional "custody" and "visitation" terminology rather than modern parenting time language',
        'The state requires detailed custody orders in all cases involving minor children',
        'Iowa has a strong preference for maintaining meaningful relationships between children and both parents',
        'Relocation with children requires court approval or written consent from the other parent for moves over 150 miles',
        'Iowa courts emphasize stability and continuity in the child\'s living arrangements',
        'The state has mandatory mediation programs available in most judicial districts'
      ],
      uniqueFeatures: [
        'Iowa has specific agricultural family provisions considering seasonal work demands',
        'The state allows for informal custody agreements that can be formalized by the court',
        'Iowa recognizes the challenges of rural geography and farming schedules in custody arrangements',
        'The state has specific statutory provisions for grandparent visitation rights',
        'Iowa allows for modification of custody arrangements based on changed circumstances',
        'The state considers domestic violence history very seriously in custody determinations',
        'Iowa has integrated family court systems in larger counties with specialized judges'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Iowa for at least 90 days before filing, or Iowa must be the child\'s home state under UCCJEA',
      filing: 'File in the county where either parent resides or where the child has lived for the past 6 months',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include petition, notice, financial affidavit, and proposed custody arrangement in all cases involving children',
      uniqueRules: [
        'Iowa requires financial disclosure in all family law cases involving children or support',
        'Mediation is strongly encouraged and available in most judicial districts',
        'Guardian ad litem appointment is discretionary based on case circumstances and child\'s best interests',
        'Parent education programs may be required in some counties for divorcing parents',
        'Iowa allows temporary custody orders for emergency situations involving child welfare'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Iowa uses "physical care" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) requires court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "visitation rights" - includes specific schedules for the non-custodial parent\'s time with the child',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety or welfare concerns exist',
        'Long-distance visitation arrangements are common due to Iowa\'s agricultural nature',
        'Extended summer visitation is standard to accommodate school schedules and farming seasons',
        'Holiday visitation typically alternates between parents on annual basis',
        'Virtual visitation (video calls) is increasingly recognized for long-distance and rural situations',
        'Grandparent visitation rights are protected under Iowa Code § 598.35'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed custody and visitation schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during visitation',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child\'s residence',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include detailed provisions for Iowa\'s agricultural seasons and how they affect work schedules',
        'Address how to handle missed visitation due to weather, farming demands, or emergencies',
        'Include specific language about travel distances and meeting locations for exchanges in rural areas',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual visitation provisions for rural families',
        'Plan for extended summer arrangements and school break schedules'
      ]
    },
    childSupport: {
      guidelines: 'Iowa uses a modified Income Shares Model based on both parents\' net income with Iowa-specific guidelines and cost-of-living adjustments',
      factors: [
        'Net monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Visitation time and overnight stays with paying parent (if significant)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support',
        'Iowa\'s rural cost of living and agricultural income considerations'
      ],
      calculation: 'Iowa Child Support Guidelines using Income Shares Model with state-specific adjustments for rural and agricultural families',
      modifications: 'Substantial change in circumstances required, typically 10% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through Iowa Department of Human Services including wage garnishment, asset seizure, tax intercept, and license suspension'
    },
    resources: {
      courts: [
        {
          name: 'Iowa Judicial Branch Family Court Resources',
          url: 'https://www.iowacourts.gov/for-the-public/representing-yourself/family-law/',
          description: 'Official Iowa court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Iowa Family Court Forms',
          url: 'https://www.iowacourts.gov/for-the-public/court-forms/',
          description: 'Complete collection of Iowa family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Iowa Self-Help Legal Resources',
          url: 'https://www.iowacourts.gov/for-the-public/representing-yourself/',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'Polk County District Court',
          address: '111 Court Avenue, Des Moines, IA 50309',
          phone: '(515) 286-3772',
          description: 'Family court serving Des Moines area and Polk County'
        },
        {
          name: 'Linn County District Court',
          address: '50 Third Avenue Bridge, Cedar Rapids, IA 52401',
          phone: '(319) 398-3920',
          description: 'Family court serving Cedar Rapids area and Linn County'
        },
        {
          name: 'Scott County District Court',
          address: '428 Western Avenue, Davenport, IA 52801',
          phone: '(563) 326-8783',
          description: 'Family court serving Davenport area and Scott County'
        }
      ],
      support: [
        {
          name: 'Iowa Department of Human Services - Child Support Recovery Unit',
          url: 'https://dhs.iowa.gov/child-support',
          phone: '1-888-229-9223',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Iowa Child Support Payment Center',
          url: 'https://www.iowacollect.com/',
          description: 'Official payment processing and case management portal for Iowa child support'
        },
        {
          name: 'Iowa Legal Aid',
          url: 'https://www.iowalegalaid.org/',
          phone: '1-800-532-1503',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'Iowa State Bar Association Family Law Section',
          url: 'https://www.iowabar.org/sections/family-law',
          description: 'Professional resources and directory of family law attorneys in Iowa'
        },
        {
          name: 'Iowa Coalition Against Domestic Violence',
          url: 'https://www.icadv.org/',
          phone: '1-800-942-0333',
          description: 'Statewide domestic violence resources, shelters, and legal advocacy services'
        }
      ],
      legal: [
        {
          name: 'Iowa State Bar Association Lawyer Referral Service',
          url: 'https://www.iowabar.org/public/lawyer-referral',
          phone: '(515) 243-3179',
          description: 'Professional attorney referrals for family law cases throughout Iowa'
        },
        {
          name: 'University of Iowa College of Law Legal Clinic',
          url: 'https://law.uiowa.edu/clinical-programs',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Drake University Law School Legal Clinic',
          url: 'https://law.drake.edu/clinics/',
          description: 'Clinical legal services including family law representation'
        },
        {
          name: 'Iowa Collaborative Law Professionals',
          url: 'https://www.collaborativelaw.org/find-a-professional/iowa/',
          description: 'Network of collaborative divorce attorneys and professionals in Iowa'
        }
      ],
      educational: [
        {
          name: 'Children First Iowa',
          url: 'https://www.iowacourts.gov/for-the-public/representing-yourself/family-law/children-first/',
          description: 'Parent education program for divorcing parents with minor children (required in some counties)'
        },
        {
          name: 'Iowa Parenting Plan Resources',
          url: 'https://www.iowacourts.gov/for-the-public/representing-yourself/family-law/custody/',
          description: 'Comprehensive guide to creating effective parenting plans in Iowa'
        },
        {
          name: 'Iowa Guardian ad Litem Program',
          url: 'https://www.iowacourts.gov/for-the-public/court-appointed-advocates/',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'Iowa State University Extension - Family Life',
          url: 'https://www.extension.iastate.edu/families/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        }
      ]
    },
    bestInterestsFactors: [
      'Whether each parent would be a suitable custodian for the child',
      'Whether the psychological and emotional needs and development of the child will be met',
      'Whether the child will have reasonable access to both parents',
      'Whether there is any evidence of domestic abuse',
      'Whether the safety of the child, other children, or the other parent is at risk',
      'Whether each parent can support the other parent\'s relationship with the child',
      'Whether the custody arrangement is in accordance with the child\'s wishes or whether the child has strong opposition, taking into consideration the child\'s age and maturity',
      'Whether one or both parents agree or are opposed to joint custody',
      'The geographic proximity of the parents',
      'Whether the psychological and emotional needs and development of the child will be met by the arrangement',
      'The safety of all parties involved',
      'Any other relevant factor the court deems important'
    ],
    practicalTips: [
      'Iowa courts value stability and consistency - document your regular involvement in your child\'s life',
      'Keep detailed records of all visitation time, communications with the other parent, and child-related expenses',
      'Be prepared for potential long-distance travel due to Iowa\'s rural nature and agricultural communities',
      'Weather conditions and farming seasons can significantly impact visitation - include flexible provisions',
      'Iowa courts appreciate parents who can demonstrate cooperation and effective communication',
      'Consider hiring a local attorney familiar with Iowa judges and court procedures',
      'Take advantage of extended summer visitation opportunities common in Iowa custody arrangements',
      'Document any special circumstances related to agricultural work, seasonal employment, or rural living conditions'
    ]
  }
};

export default iowaData;